import {getCurrentUserLogin} from '@github-ui/client-env'

import {useLayoutAuthorLogin} from '../../../../stores/layout-selectors'
import {useAssigneesQuery} from '../../data/route-queries'

export interface ForYouViewerRoles {
  /** Viewer authored the pull request. */
  isAuthor: boolean
  /** Viewer is an assignee on the pull request. */
  isAssignee: boolean
  /** Viewer is neither the author nor an assignee. */
  isReviewer: boolean
}

export interface ForYouViewerRolesQueryResult extends ForYouViewerRoles {
  /** Whether the assignees query has enough data to determine the viewer's roles. */
  queryStatus: 'loading' | 'error' | 'missing' | 'success'
}

/**
 * Classifies the current viewer relative to the pull request for analytics and
 * audience gating. Roles are independent booleans — an author can also be an
 * assignee. "Reviewer" is the catch-all for anyone who owns neither role.
 */
export function useViewerRoles(): ForYouViewerRolesQueryResult {
  const authorLogin = useLayoutAuthorLogin()
  const viewerLogin = getCurrentUserLogin()
  const {data: assigneesData, isError, isLoading} = useAssigneesQuery()

  if (!viewerLogin) {
    return {isAuthor: false, isAssignee: false, isReviewer: false, queryStatus: 'success'}
  }

  if (!assigneesData) {
    return {
      isAuthor: false,
      isAssignee: false,
      isReviewer: false,
      queryStatus: isLoading ? 'loading' : isError ? 'error' : 'missing',
    }
  }

  const isAuthor = authorLogin === viewerLogin
  const isAssignee = (assigneesData.assignees ?? []).some(assignee => assignee.displayLogin === viewerLogin)

  return {isAuthor, isAssignee, isReviewer: !isAuthor && !isAssignee, queryStatus: 'success'}
}
